import { useState, useMemo } from 'react';
import { Marker, InfoWindow } from '@react-google-maps/api';
import { Box, Typography, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Parking } from '../../resources/types/parkingsTypes';

interface Props {
  parking: Parking;
}

const getPhotoUrl = (photoPath?: string) => {
  if (!photoPath) return null;
  return `https://whee.pro/public/${photoPath}`;
};

const getInitials = (name: string): string => {
  if (!name) return '?';
  const cleaned = name.trim();
  const words = cleaned.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return cleaned.slice(0, 2).toUpperCase();
};

const getPolygonCenter = (coordinates: number[][]): { lat: number; lng: number } | null => {
  if (!coordinates || coordinates.length === 0) return null;
  let sumLat = 0;
  let sumLng = 0;
  coordinates.forEach((c) => {
    sumLat += c[0];
    sumLng += c[1];
  });
  return {
    lat: sumLat / coordinates.length,
    lng: sumLng / coordinates.length
  };
};

const buildMarkerIcon = (initials: string): google.maps.Icon | undefined => {
  if (typeof window === 'undefined' || !window.google?.maps) return undefined;
  const text = initials;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="44" viewBox="0 0 36 44">
      <defs>
        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="1" stdDeviation="1" flood-opacity="0.35"/>
        </filter>
      </defs>
      <path d="M18 1 C9 1 2 8 2 17 C2 28 18 43 18 43 C18 43 34 28 34 17 C34 8 27 1 18 1 Z"
        fill="#4ECDC4" stroke="#FFFFFF" stroke-width="2" filter="url(#shadow)"/>
      <circle cx="18" cy="17" r="11" fill="#FFFFFF"/>
      <text x="18" y="21" text-anchor="middle" font-family="Inter, Arial, sans-serif"
        font-size="11" font-weight="700" fill="#3BA99F">${text}</text>
    </svg>
  `;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(36, 44),
    anchor: new window.google.maps.Point(18, 43)
  };
};

const ParkingMarker = ({ parking }: Props) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  const center = useMemo(() => getPolygonCenter(parking.coordinates), [parking.coordinates]);
  const initials = useMemo(() => getInitials(parking.name), [parking.name]);
  const icon = useMemo(() => buildMarkerIcon(initials), [initials]);
  const photoUrl = getPhotoUrl(parking.photo);

  if (!center) return null;

  return (
    <Marker
      position={center}
      icon={icon}
      onClick={() => setOpen(true)}
      zIndex={1}
    >
      {open && (
        <InfoWindow position={center} onCloseClick={() => setOpen(false)}>
          <Box sx={{ minWidth: 220, maxWidth: 280, p: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: '#3BA99F' }}>
              {parking.name}
            </Typography>

            {parking.address && (
              <Typography variant="body2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
                {parking.address}
              </Typography>
            )}

            {photoUrl ? (
              <Box
                component="img"
                src={photoUrl}
                alt={parking.name}
                sx={{
                  width: '100%',
                  height: 140,
                  objectFit: 'cover',
                  borderRadius: '8px',
                  mb: 1,
                  display: 'block'
                }}
                onError={(e: any) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: 100,
                  borderRadius: '8px',
                  bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1
                }}
              >
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  Без фото
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              <Chip
                size="small"
                label={`Самокатів: ${parking.scooters?.length ?? 0}`}
                sx={{ bgcolor: 'rgba(78,205,196,0.15)', color: '#3BA99F', fontWeight: 600 }}
              />
            </Box>
          </Box>
        </InfoWindow>
      )}
    </Marker>
  );
};

export default ParkingMarker;
